/**
 * The application-wide provider stack.
 *
 * Order is load-bearing: SessionExpiredBanner calls `useNavigate`, so it has to
 * mount inside the router subtree, and the banners sit inside AuthProvider
 * because they read auth state. Everything below QueryClientProvider needs the
 * query client.
 *
 * This component must still render inside <BrowserRouter>: SessionExpiredBanner
 * calls `useNavigate`. (It used to also read `useLocation` to animate the grid
 * on auth pages; the instrument grid has pointer parallax instead, so the
 * upward-drift animation and its `animate` prop are gone.)
 */
import type { ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@ai-tutor/ui';

import { ShellBanners } from '@/components/features/shell/ShellBanners';
import { GridBackground } from '@/components/core/common/GridBackground';
import { BudgetExceededDialog } from '@/components/features/billing/dialogs/BudgetExceededDialog';
import { BreakSheetContainer } from '@/components/features/wellbeing/focus/BreakSheetContainer';
import { FocusPillMenu } from '@/components/features/wellbeing/focus/FocusPillMenu';
import { PodcastPillMenu } from '@/components/features/learning/podcast/PodcastPillMenu';
import { queryClient } from '@/config/query-client';
import { defaultTheme } from '@/config/theme';
import { AuthProvider } from '@/contexts/AuthContext';
import { DataModeProvider } from '@/contexts/DataModeContext';
import { FocusTimerProvider } from '@/contexts/FocusTimerContext';
import { PodcastPlayerProvider } from '@/contexts/PodcastPlayerContext';
import { ShellSlotsProvider } from '@/contexts/ShellSlotsContext';
import { ThemeProvider } from '@/contexts/ThemeProvider';

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <>
      <GridBackground
        // Flat indigo ground from the `--ground` token. The landing page keeps
        // its own three-stop gradient; the app is deliberately flat, so the
        // indigo lattice sits on one even field instead of drifting in
        // contrast across the page.
        className={`min-h-screen bg-ground`}
      >
        <QueryClientProvider client={queryClient}>
          <DataModeProvider>
            {/* `AppProvider` and `AppStateProvider` used to wrap here. Both
              were reducer contexts with ZERO production consumers (only
              tests read `useApp`/`useAppState`), holding duplicated copies
              of state that AuthContext, ThemeProvider, React Query,
              lib/notify and ErrorBoundary already own — each with its own
              local `User` type. They were the last layer of a
              pre-React-Query architecture; server state does not belong in
              context. Removed, 307 lines. */}
            <ThemeProvider defaultTheme={defaultTheme}>
              <AuthProvider>
                {/* App-wide status strips (offline, session expired,
                  service issues, billing), stacked in one fixed container.
                  SessionExpired needs the router (it uses `useNavigate`), so
                  the stack must mount inside <AuthProvider>'s subtree. */}
                <ShellBanners />
                {/* The Pomodoro timer runs app-wide, above the routes, so a
                  block keeps counting as the student moves between pages.
                  The Navbar shows it through a slot (core/ cannot import a
                  feature), and the break sheet opens over any page. */}
                <FocusTimerProvider sheet={<BreakSheetContainer />}>
                  {/* The podcast player is app-wide for the same reason: a
                    section's podcast keeps playing across pages, shown as a
                    second pill beside the timer. */}
                  <PodcastPlayerProvider>
                    <ShellSlotsProvider
                      navbarAccessory={
                        <>
                          <FocusPillMenu />
                          <PodcastPillMenu />
                        </>
                      }
                    >
                      {children}
                    </ShellSlotsProvider>
                  </PodcastPlayerProvider>
                </FocusTimerProvider>
              </AuthProvider>
            </ThemeProvider>
          </DataModeProvider>
          {/* Global paywall - subscribes to `budget-exceeded` events
            emitted by api-client when the proxy returns 402 with the
            BudgetExceeded payload. Mounted once, app-wide. */}
          <BudgetExceededDialog />
        </QueryClientProvider>
      </GridBackground>
      {/* Toast notifications. The configuration used to live here inline,
          carrying twelve hardcoded hex values and no dark variants at all;
          it is now a tokenised primitive in `@ai-tutor/ui`.

          Rendered OUTSIDE GridBackground: its content wraps children in a
          `relative z-10` stacking context, which would trap the toast's
          fixed z-index below body-portaled modals (z-50). At the fragment
          root the toast sits above modals, as intended. */}
      <Toaster />
    </>
  );
}
